import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { createContext, useContext, useEffect, useMemo } from 'react';
import {
  ROUTE_LABS_ORDER_STEP_1,
  ROUTE_LABS_ORDER_STEP_2,
  ROUTE_LABS_ORDER_STEP_3,
  ROUTE_LABS_ORDER_STEP_4,
  ROUTE_LABS_ORDER_STEP_5,
} from '../../../constants/routes';
import { useFullScreen } from '@/queries/layout.queries';
import {
  useListLabOrdersV1LabsOrdersGet,
  useGetLabOrderV1LabsOrdersOrderIdGet,
} from '@/api/generated-catch-backend/laboratory-orders/laboratory-orders';
import type { LabOrderResponse } from '@/api/generated-catch-backend/model';
import { OrderStatus } from '@/api/generated-catch-backend/model';
import useAnalyticsCalls from '@/hooks/use-analytics';

// Form schema for all steps
export const labsOrderSchema = z.object({
  // Step 2: User information
  firstName: z
    .string()
    .min(1, 'First name is required')
    .regex(
      /^[a-zA-Z0-9][a-zA-Z0-9\s.\-']*$/,
      'First name must start with a letter or number and can only contain letters, numbers, spaces, hyphens, periods, and apostrophes'
    ),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .regex(
      /^[a-zA-Z0-9][a-zA-Z0-9\s.\-']*$/,
      'Last name must start with a letter or number and can only contain letters, numbers, spaces, hyphens, periods, and apostrophes'
    ),
  email: z.string().email('Please enter a valid email address'),
  phoneNumber: z.string().regex(
    /^\+?1?\d{10,14}$/,
    'Please enter a valid phone number'
  ),
  gender: z.enum(['male', 'female', 'other', 'unknown'], {
    errorMap: () => ({ message: 'Please select a gender' })
  }),
  dateOfBirth: z.string().min(1, 'Date of birth is required'),
  address: z.object({
    street: z
      .string()
      .min(1, 'Street address is required')
      .regex(
        /^[\x20-\x7E]+$/,
        'Address cannot contain special characters or emojis. Please use only letters, numbers, and basic punctuation.'
      ),
    apartmentSuite: z
      .string()
      .regex(
        /^[\x20-\x7E]*$/,
        'Address cannot contain special characters or emojis. Please use only letters, numbers, and basic punctuation.'
      )
      .optional(),
    city: z
      .string()
      .min(1, 'City is required')
      .regex(
        /^[\x20-\x7E]+$/,
        'City cannot contain special characters or emojis. Please use only letters, numbers, and basic punctuation.'
      ),
    state: z.string().min(2, 'State is required'),
    zipCode: z.string().regex(/^\d{5}(-\d{4})?$/, 'Please enter a valid ZIP code'),
  }),
});

export type LabsOrderFormData = z.infer<typeof labsOrderSchema>;

// Context to share order data across child routes
interface LabOrderContextValue {
  activeOrder?: LabOrderResponse;
  isLoadingOrder: boolean;
  refetchOrder: () => void;
  refetchOrdersList: () => void;
}

export const LabOrderContext = createContext<LabOrderContextValue | undefined>(undefined);

export function useLabOrder() {
  const context = useContext(LabOrderContext);
  if (!context) {
    throw new Error('useLabOrder must be used within LabOrderContext.Provider');
  }
  return context;
}

/**
 * Maps an order status to the appropriate step number in the order flow.
 *
 * Status progression:
 * - No order → Step 1 (start new order)
 * - patient_details_collected → Step 3 (select lab)
 * - psc_selected → Step 4 (provide consent)
 * - order_created_pending_signature → Step 4 (complete consent)
 * - requisition_created and beyond → Step 5 (success/view order)
 *
 * @param orderStatus - The current order status, or undefined if no order exists
 * @returns The step number the user should be on
 */
function getCorrectStepForOrderStatus(orderStatus: OrderStatus | undefined): number {
  if (!orderStatus) {
    return 1; // No order exists, start at step 1
  }

  switch (orderStatus) {
    case OrderStatus.patient_details_collected:
      // User has entered their details, next step is to select a lab
      return 3;

    case OrderStatus.psc_selected:
      // User has selected a lab, needs to provide/complete consent
      return 4;

    // Terminal/success statuses - show success page
    case OrderStatus.order_created_pending_signature:
    case OrderStatus.requisition_created:
    case OrderStatus.sample_collected:
    case OrderStatus.sample_with_lab:
    case OrderStatus.partial_results:
    case OrderStatus.completed:
    case OrderStatus.processed:
      return 5;

    // Failed/cancelled orders - show success page with error UI
    case OrderStatus.cancelled:
    case OrderStatus.failed:
      return 5;

    default:
      // Fallback to step 1 for unknown statuses
      return 1;
  }
}

export function Page() {
  useFullScreen();
  const location = useLocation();
  const navigate = useNavigate();
  const { setAnalyticsParams } = useAnalyticsCalls();

  // Fetch list of orders to find if there's an active one
  const { data: ordersData, isLoading: isLoadingOrders, refetch: refetchOrdersList } = useListLabOrdersV1LabsOrdersGet({
    query: {
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    },
  });

  // Find the most recent active order (not cancelled or completed)
  const activeOrderId = useMemo(() => {
    if (!ordersData?.data) return undefined;

    const activeOrder = ordersData.data.find(
      (order) =>
        order.status !== OrderStatus.cancelled &&
        order.status !== OrderStatus.completed &&
        order.status !== OrderStatus.processed
    );

    return activeOrder?.orderId;
  }, [ordersData]);

  const {
    data: activeOrderData,
    isLoading: isLoadingOrderDetails,
    refetch: refetchOrder,
  } = useGetLabOrderV1LabsOrdersOrderIdGet(activeOrderId ?? '', {
    query: {
      enabled: !!activeOrderId,
      staleTime: 0,
      gcTime: 0,
      refetchOnMount: 'always',
      refetchOnWindowFocus: true,
    },
  });

  const activeOrder = activeOrderId ? activeOrderData : undefined;

  const isLoadingOrder = isLoadingOrders || isLoadingOrderDetails;

  const methods = useForm<LabsOrderFormData>({
    resolver: zodResolver(labsOrderSchema),
    mode: 'onBlur',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      gender: undefined as unknown as 'male' | 'female' | 'other' | 'unknown',
      dateOfBirth: '',
      address: {
        street: '',
        apartmentSuite: '',
        city: '',
        state: '',
        zipCode: '',
      },
    },
  });

  // Centralized routing logic - redirect to correct step based on order status
  useEffect(() => {
    setAnalyticsParams({ catch_panel: 'standard_cancer_panel' });

    // Don't run routing logic if still loading
    if (isLoadingOrder) {
      return;
    }

    // Extract current step from pathname (e.g., /labs/order/step-3 → 3)
    const stepMatch = location.pathname.match(/\/labs\/order\/step-(\d+)/);
    if (!stepMatch) {
      // Not on a step page, let the base redirect handle it
      return;
    }

    const currentStep = parseInt(stepMatch[1], 10);
    const correctStep = getCorrectStepForOrderStatus(activeOrder?.status);

    // Special case: If no order exists and user is on step 3+, redirect to step 1
    // Step 2 is allowed without an order because that's where the order is created
    // This handles manual navigation to steps without having an order
    if (!activeOrder && currentStep > 2) {
      navigate(ROUTE_LABS_ORDER_STEP_1, { replace: true });
      return;
    }

    // Allow step 2 without an order (order is created there)
    if (!activeOrder && currentStep === 2) {
      return;
    }

    // Only redirect if user is trying to skip forward
    // Allow backward navigation (user can go back to modify previous steps)
    if (currentStep > correctStep) {
      const stepRoutes = [
        '',  // Index 0 (unused)
        ROUTE_LABS_ORDER_STEP_1,
        ROUTE_LABS_ORDER_STEP_2,
        ROUTE_LABS_ORDER_STEP_3,
        ROUTE_LABS_ORDER_STEP_4,
        ROUTE_LABS_ORDER_STEP_5,
      ];
      navigate(stepRoutes[correctStep], { replace: true });
    }
  }, [activeOrder, isLoadingOrder, location.pathname, navigate]);

  // Redirect to step 1 if at base /labs/order path
  if (location.pathname === '/labs/order') {
    return <Navigate to={ROUTE_LABS_ORDER_STEP_1} replace />;
  }

  const contextValue: LabOrderContextValue = {
    activeOrder,
    isLoadingOrder,
    refetchOrder,
    refetchOrdersList,
  };

  return (
    <LabOrderContext.Provider value={contextValue}>
      <FormProvider {...methods}>
        <Outlet />
      </FormProvider>
    </LabOrderContext.Provider>
  );
}
